import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-ds-service-listing',
    standalone: true,
    imports: [
        TranslateModule,
        MatIconModule,
        CommonModule,
        FormsModule,
        SlickCarouselModule,
    ],
    templateUrl: './ds-service-listing.component.html',
    styleUrls: ['./ds-service-listing.component.scss'],
})
export class DsServiceListingComponent {
    imageBaseUrl = environment.imageBaseUrl;
    @Input() selectedLanguage: string;
    @Input() digiServiceData: any[] = [];
    initialDigiServiceData: any[] = [];
    @Output() openDigiPartnershipDialog: any = new EventEmitter<any>();
    searchText: string = '';
    resultCountText: string = '';
    resultCountTextBM: string = '';
    sortOptions = [
        { key: 'alphabetical', label: 'DIGITAL_SERVICES.ALPHABETICAL' },
    ];
    showFilter = false;
    filterCategories: string[] = ['Telecommunication'];
    selectedFilterCategories: Set<string> = new Set();
    showSort = false;
    selectedSortOption: string | null = null;
    @ViewChild('filterMenu', { static: false }) filterMenuRef!: ElementRef;
    @ViewChild('filterButton', { static: false }) filterButtonRef!: ElementRef;
    @ViewChild('sortMenu', { static: false }) sortMenuRef!: ElementRef;
    @ViewChild('sortButton', { static: false }) sortButtonRef!: ElementRef;
    displayCount = 8;
    initialCount = 8;

    slideConfig = {
        infinite: true,
        slidesToShow: 4,
        slidesToScroll: 1,
        dots: false,
        prevArrow:
            '<button class="slick-prev"><img src="/assets/images/home/left-arrow.png" alt="Previous" /></button>',
        nextArrow:
            '<button class="slick-next"><img src="/assets/images/home/right-arrow.png" alt="Next" /></button>',
        responsive: [
            {
                breakpoint: 1280,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 900,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    constructor(private router: Router) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['digiServiceData']) {
            const normalized = this.normalizeDigiServiceData(
                changes['digiServiceData'].currentValue,
            );
            this.initialDigiServiceData = normalized.filter(
                (item) => item.status === 'ACTIVE',
            );
            this.digiServiceData = [...this.initialDigiServiceData];
        }
    }

    private normalizeDigiServiceData(rawData: any[]): any[] {
        return (rawData || []).map((item) => {
            const details = item.details || [];
            const enDetail = details.find((d: any) => d.language === 'en');
            const bmDetail = details.find((d: any) => d.language === 'bm');

            return {
                ...item,
                digPartnershipName: item.digpartnershipname,
                partnershipUrl: item.partnershipurl,
                partnershipImageUrl: item.partnershipimageurl,
                en: this.normalizeDetail(enDetail),
                bm: this.normalizeDetail(bmDetail),
            };
        });
    }

    private normalizeDetail(detail: any): any {
        if (!detail) return null;
        return {
            digPartnershipName: detail.digpartnershipname,
            digProductName: detail.digproductname,
            digTitle: detail.digtitle,
            digShortDescription: detail.digshortdescription,
            digLongDescription: detail.diglongdescription,
            cta: detail.cta,
            plans: detail.plans,
        };
    }

    onSearch(): void {
        this.selectedFilterCategories.clear();
        this.selectedSortOption = null;
        const text = this.searchText?.trim().toLowerCase() || '';

        if (!text) {
            this.digiServiceData = [...this.initialDigiServiceData];
            this.resultCountText = '';
            this.resultCountTextBM = '';
            return;
        }

        this.digiServiceData = this.initialDigiServiceData.filter(
            (item) =>
                item.digPartnershipName?.toLowerCase().includes(text) ||
                item.en.digPartnershipName?.toLowerCase().includes(text) ||
                item.bm.digPartnershipName?.toLowerCase().includes(text) ||
                item.en.digProductName?.toLowerCase().includes(text) ||
                item.bm.digProductName?.toLowerCase().includes(text) ||
                item.en.digShortDescription?.toLowerCase().includes(text) ||
                item.bm.digShortDescription?.toLowerCase().includes(text),
        );

        const count = this.digiServiceData.length;
        this.resultCountText =
            count === 0
                ? 'No results found for this search'
                : `${count} result${count > 1 ? 's' : ''} found`;
        this.resultCountTextBM =
            count === 0
                ? 'Tiada hasil carian ditemui'
                : `${count} keputusan dijumpai`;
    }

    toggleFilterBy() {
        this.showFilter = !this.showFilter;
        if (this.showFilter) {
            this.showSort = false;
        }
    }

    onClickFilterOption(category: string): void {
        if (this.searchText.trim()) {
            this.searchText = '';
            this.digiServiceData = [...this.initialDigiServiceData];
        }

        this.selectedFilterCategories.has(category)
            ? this.selectedFilterCategories.delete(category)
            : this.selectedFilterCategories.add(category);

        this.applyFiltersAndSort();
    }

    applyFiltersAndSort(): void {
        let result = [...this.initialDigiServiceData];
        if (this.selectedFilterCategories.size > 0) {
            result = result.filter((item) =>
                this.selectedFilterCategories.has(item.category),
            );
        }

        const selectedOption: any = this.sortOptions.find(
            (opt) => opt.key === this.selectedSortOption,
        );
        if (selectedOption) {
            const prop = 'digPartnershipName';

            result.sort((a, b) => {
                const valA = a[prop];
                const valB = b[prop];

                if (!valA && !valB) return 0;
                if (!valA) return 1;
                if (!valB) return -1;

                return valA.localeCompare(valB);
            });
        }
        this.digiServiceData = result;
    }

    toggleSortBy() {
        this.showSort = !this.showSort;
        if (this.showSort) {
            this.showFilter = false;
        }
    }
    onClickSortOption(optionKey: string) {
        if (this.searchText.trim()) {
            this.searchText = '';
            this.digiServiceData = [...this.initialDigiServiceData];
        }
        if (this.selectedSortOption === optionKey) {
            this.selectedSortOption = null;
            this.digiServiceData = [...this.initialDigiServiceData];
        } else {
            this.selectedSortOption = optionKey;
            if (optionKey === 'alphabetical') {
                this.digiServiceData = [...this.digiServiceData].sort((a, b) =>
                    a.digPartnershipName.localeCompare(b.digPartnershipName),
                );
            }
        }
        this.applyFiltersAndSort();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event) {
        const clickedInsideSortMenu = this.sortMenuRef?.nativeElement.contains(
            event.target,
        );
        const clickedSortButton = this.sortButtonRef?.nativeElement.contains(
            event.target,
        );

        if (!clickedInsideSortMenu && !clickedSortButton) {
            this.showSort = false;
        }

        const clickedInsideFilterMenu =
            this.filterMenuRef?.nativeElement.contains(event.target);
        const clickedFilterButton =
            this.filterButtonRef?.nativeElement.contains(event.target);

        if (!clickedInsideFilterMenu && !clickedFilterButton) {
            this.showFilter = false;
        }
    }

    getPartnerData(data: any): any {
        return this.selectedLanguage === 'en' ? data?.en : data?.bm;
    }

    onClickPartner(data) {
        const selectedDigiPartner = this.getPartnerData(
            data.partnershipDetails,
        );

        const mergedDigiPartner = {
            ...selectedDigiPartner,
            partnershipUrl: data.partnershipUrl,
        };
        this.openDigiPartnershipDialog.emit(mergedDigiPartner);
    }

    showMore() {
        const remaining = this.digiServiceData.length - this.displayCount;
        this.displayCount += remaining >= 8 ? 8 : remaining;
    }

    redirectPartner(data, url: string) {
        if (data?.en?.digPartnershipName !== 'Bukku') {
            window.open(url, '_blank');
        } else {
            const segments = url?.split('/').slice(-2)?.join('/');
            this.router.navigate([`/${segments}`], {
                state: {
                    data: data,
                },
            });
        }
    }

    getSlug(data: any) {
        const slug = data?.en?.digProductName
            ?.replaceAll(' ', '-')
            .toLowerCase();
        return `card_${slug}_digitalservices_click`;
    }

    getImageUrl(imageUrl: string): string {
        return this.imageBaseUrl + imageUrl;
    }
}
